/**
 * Storage Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides safe localStorage and sessionStorage operations
 * with error handling and data validation.
 */

import { STORAGE_KEYS } from '../constants';
import { objectHelpers, browserHelpers } from './helpers';

/**
 * Storage availability check
 */
const storageAvailable = {
  localStorage: false,
  sessionStorage: false
};

/**
 * Check if storage is available
 * @param {string} type - Storage type ('localStorage' or 'sessionStorage')
 * @returns {boolean} True if storage is available
 */
function checkStorageAvailability(type) {
  try {
    const storage = window[type];
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
}

// Initialize storage availability
if (typeof window !== 'undefined') {
  storageAvailable.localStorage = checkStorageAvailability('localStorage');
  storageAvailable.sessionStorage = checkStorageAvailability('sessionStorage');
}

/**
 * LocalStorage Operations
 */
export const localStorage = {
  /**
   * Set item in localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {boolean} True if successful
   */
  setItem: function(key, value) {
    if (!storageAvailable.localStorage) {
      console.warn('localStorage not available');
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      window.localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('localStorage setItem error:', error);
      return false;
    }
  },

  /**
   * Get item from localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if not found
   * @returns {any} Stored value or default
   */
  getItem: function(key, defaultValue) {
    if (!storageAvailable.localStorage) {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (item === null) return defaultValue;

      return JSON.parse(item);
    } catch (error) {
      console.error('localStorage getItem error:', error);
      return defaultValue;
    }
  },

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} True if successful
   */
  removeItem: function(key) {
    if (!storageAvailable.localStorage) {
      return false;
    }

    try {
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('localStorage removeItem error:', error);
      return false;
    }
  },

  /**
   * Clear all localStorage items
   * @returns {boolean} True if successful
   */
  clear: function() {
    if (!storageAvailable.localStorage) {
      return false;
    }

    try {
      window.localStorage.clear();
      return true;
    } catch (error) {
      console.error('localStorage clear error:', error);
      return false;
    }
  },

  /**
   * Get all keys in localStorage
   * @returns {Array} Array of storage keys
   */
  getAllKeys: function() {
    if (!storageAvailable.localStorage) {
      return [];
    }

    try {
      const keys = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        keys.push(window.localStorage.key(i));
      }
      return keys;
    } catch (error) {
      console.error('localStorage getAllKeys error:', error);
      return [];
    }
  }
};

/**
 * SessionStorage Operations
 */
export const sessionStorage = {
  /**
   * Set item in sessionStorage with error handling
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {boolean} True if successful
   */
  setItem: function(key, value) {
    if (!storageAvailable.sessionStorage) {
      console.warn('sessionStorage not available');
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      window.sessionStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('sessionStorage setItem error:', error);
      return false;
    }
  },

  /**
   * Get item from sessionStorage with error handling
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if not found
   * @returns {any} Stored value or default
   */
  getItem: function(key, defaultValue) {
    if (!storageAvailable.sessionStorage) {
      return defaultValue;
    }

    try {
      const item = window.sessionStorage.getItem(key);
      if (item === null) return defaultValue;

      return JSON.parse(item);
    } catch (error) {
      console.error('sessionStorage getItem error:', error);
      return defaultValue;
    }
  },

  /**
   * Remove item from sessionStorage
   * @param {string} key - Storage key
   * @returns {boolean} True if successful
   */
  removeItem: function(key) {
    if (!storageAvailable.sessionStorage) {
      return false;
    }

    try {
      window.sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('sessionStorage removeItem error:', error);
      return false;
    }
  },

  /**
   * Clear all sessionStorage items
   * @returns {boolean} True if successful
   */
  clear: function() {
    if (!storageAvailable.sessionStorage) {
      return false;
    }

    try {
      window.sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('sessionStorage clear error:', error);
      return false;
    }
  }
};

/**
 * ArbiWin-Specific Storage Operations
 */
export const arbiwinStorage = {
  /**
   * Store authentication token
   * @param {string} token - JWT token
   * @param {boolean} remember - Whether to use localStorage (true) or sessionStorage (false)
   * @returns {boolean} True if successful
   */
  storeAuthToken: function(token, remember) {
    const storage = remember ? localStorage : sessionStorage;
    return storage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  /**
   * Get authentication token
   * @returns {string|null} JWT token or null
   */
  getAuthToken: function() {
    // Try localStorage first, then sessionStorage
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) return token;

    return sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  /**
   * Remove authentication token
   * @returns {boolean} True if successful
   */
  removeAuthToken: function() {
    const localSuccess = localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    const sessionSuccess = sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    return localSuccess || sessionSuccess;
  },

  /**
   * Store user data
   * @param {Object} userData - User data object
   * @returns {boolean} True if successful
   */
  storeUserData: function(userData) {
    return localStorage.setItem(STORAGE_KEYS.USER_DATA, userData);
  },

  /**
   * Get user data
   * @returns {Object|null} User data or null
   */
  getUserData: function() {
    return localStorage.getItem(STORAGE_KEYS.USER_DATA);
  },

  /**
   * Remove user data
   * @returns {boolean} True if successful
   */
  removeUserData: function() {
    return localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  },

  /**
   * Store user preferences
   * @param {Object} preferences - User preferences object
   * @returns {boolean} True if successful
   */
  storeUserPreferences: function(preferences) {
    return localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, preferences);
  },

  /**
   * Get user preferences
   * @param {Object} defaultPreferences - Default preferences
   * @returns {Object} User preferences
   */
  getUserPreferences: function(defaultPreferences) {
    return localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES, defaultPreferences || {});
  },

  /**
   * Store language preference
   * @param {string} language - Language code (bg/en)
   * @returns {boolean} True if successful
   */
  storeLanguage: function(language) {
    return localStorage.setItem(STORAGE_KEYS.LANGUAGE, language);
  },

  /**
   * Get language preference
   * @returns {string} Language code or default (bg)
   */
  getLanguage: function() {
    return localStorage.getItem(STORAGE_KEYS.LANGUAGE, 'bg');
  },

  /**
   * Store bookmaker filters
   * @param {Array} bookmakers - Selected bookmaker IDs
   * @returns {boolean} True if successful
   */
  storeBookmakerFilters: function(bookmakers) {
    return localStorage.setItem(STORAGE_KEYS.BOOKMAKER_FILTERS, bookmakers);
  },

  /**
   * Get bookmaker filters
   * @returns {Array} Selected bookmaker IDs
   */
  getBookmakerFilters: function() {
    return localStorage.getItem(STORAGE_KEYS.BOOKMAKER_FILTERS, []);
  },

  /**
   * Store last refresh timestamp
   * @param {Date|string} timestamp - Refresh timestamp
   * @returns {boolean} True if successful
   */
  storeLastRefresh: function(timestamp) {
    const timestampStr = timestamp instanceof Date ? timestamp.toISOString() : timestamp;
    return localStorage.setItem(STORAGE_KEYS.LAST_REFRESH, timestampStr);
  },

  /**
   * Get last refresh timestamp
   * @returns {Date|null} Last refresh date or null
   */
  getLastRefresh: function() {
    const timestamp = localStorage.getItem(STORAGE_KEYS.LAST_REFRESH);
    return timestamp ? new Date(timestamp) : null;
  },

  /**
   * Store calculator history
   * @param {Array} calculations - Array of calculation objects
   * @returns {boolean} True if successful
   */
  storeCalculatorHistory: function(calculations) {
    // Limit history to last 50 calculations
    const limitedHistory = Array.isArray(calculations) ? calculations.slice(-50) : [];
    return localStorage.setItem(STORAGE_KEYS.CALCULATOR_HISTORY, limitedHistory);
  },

  /**
   * Get calculator history
   * @returns {Array} Array of calculation objects
   */
  getCalculatorHistory: function() {
    return localStorage.getItem(STORAGE_KEYS.CALCULATOR_HISTORY, []);
  },

  /**
   * Clear all ArbiWin-related storage
   * @returns {boolean} True if successful
   */
  clearAllArbiWinData: function() {
    let success = true;

    // Remove all ArbiWin-specific keys
    Object.values(STORAGE_KEYS).forEach(function(key) {
      if (!localStorage.removeItem(key)) {
        success = false;
      }
      if (!sessionStorage.removeItem(key)) {
        success = false;
      }
    });

    return success;
  }
};

/**
 * Storage Event Handling
 */
export const storageEvents = {
  /**
   * Listen for storage changes
   * @param {Function} callback - Callback function for storage changes
   * @returns {Function} Cleanup function
   */
  onStorageChange: function(callback) {
    if (!browserHelpers.isBrowser()) {
      return function() {}; // No-op cleanup
    }

    const handleStorageChange = function(event) {
      if (event.storageArea === window.localStorage) {
        // Check if it's an ArbiWin-related key
        const arbiwinKeys = Object.values(STORAGE_KEYS);
        if (arbiwinKeys.includes(event.key)) {
          callback({
            key: event.key,
            oldValue: event.oldValue,
            newValue: event.newValue,
            url: event.url
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Return cleanup function
    return function() {
      window.removeEventListener('storage', handleStorageChange);
    };
  },

  /**
   * Dispatch custom storage event
   * @param {string} key - Storage key that changed
   * @param {any} oldValue - Old value
   * @param {any} newValue - New value
   */
  dispatchStorageEvent: function(key, oldValue, newValue) {
    if (!browserHelpers.isBrowser()) return;

    const event = new CustomEvent('arbiwin:storageChange', {
      detail: {
        key: key,
        oldValue: oldValue,
        newValue: newValue,
        timestamp: new Date().toISOString()
      }
    });

    window.dispatchEvent(event);
  }
};

/**
 * Storage Migration Utilities
 */
export const storageMigration = {
  /**
   * Migrate storage keys from old format to new format
   * @param {Object} keyMapping - Mapping of old keys to new keys
   * @returns {boolean} True if migration successful
   */
  migrateKeys: function(keyMapping) {
    if (!storageAvailable.localStorage) return false;

    let migrated = false;

    Object.keys(keyMapping).forEach(function(oldKey) {
      const newKey = keyMapping[oldKey];
      const value = localStorage.getItem(oldKey);

      if (value !== null) {
        localStorage.setItem(newKey, value);
        localStorage.removeItem(oldKey);
        migrated = true;
      }
    });

    return migrated;
  },

  /**
   * Migrate from SureBet to ArbiWin storage keys
   * @returns {boolean} True if migration successful
   */
  migrateSureBetToArbiWin: function() {
    const keyMapping = {
      'surebet_auth_token': STORAGE_KEYS.AUTH_TOKEN,
      'surebet_refresh_token': STORAGE_KEYS.REFRESH_TOKEN,
      'surebet_user_data': STORAGE_KEYS.USER_DATA,
      'surebet_language': STORAGE_KEYS.LANGUAGE,
      'surebet_theme': STORAGE_KEYS.THEME,
      'surebet_bookmaker_filters': STORAGE_KEYS.BOOKMAKER_FILTERS,
      'surebet_user_preferences': STORAGE_KEYS.USER_PREFERENCES,
      'surebet_last_refresh': STORAGE_KEYS.LAST_REFRESH,
      'surebet_calculator_history': STORAGE_KEYS.CALCULATOR_HISTORY
    };

    return this.migrateKeys(keyMapping);
  },

  /**
   * Check storage version and migrate if needed
   * @returns {Object} Migration result
   */
  checkAndMigrate: function() {
    const currentVersion = localStorage.getItem('arbiwin_storage_version', '1.0.0');
    const targetVersion = '1.0.0';

    if (currentVersion !== targetVersion) {
      const migrationSuccess = this.migrateSureBetToArbiWin();

      if (migrationSuccess) {
        localStorage.setItem('arbiwin_storage_version', targetVersion);
      }

      return {
        migrated: migrationSuccess,
        fromVersion: currentVersion,
        toVersion: targetVersion
      };
    }

    return {
      migrated: false,
      fromVersion: currentVersion,
      toVersion: targetVersion
    };
  }
};

/**
 * Storage Utilities with Validation
 */
export const storageUtils = {
  /**
   * Store object with validation
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @param {Function} validator - Optional validation function
   * @returns {Object} Storage result
   */
  storeWithValidation: function(key, value, validator) {
    // Validate value if validator provided
    if (validator && typeof validator === 'function') {
      const validation = validator(value);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error || 'Validation failed'
        };
      }
      value = validation.value || value;
    }

    const success = localStorage.setItem(key, value);

    if (success) {
      // Dispatch storage change event
      storageEvents.dispatchStorageEvent(key, null, value);
    }

    return {
      success: success,
      error: success ? null : 'Failed to store value'
    };
  },

  /**
   * Get object with validation
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value
   * @param {Function} validator - Optional validation function
   * @returns {Object} Retrieved value with validation result
   */
  getWithValidation: function(key, defaultValue, validator) {
    let value = localStorage.getItem(key, defaultValue);

    // Validate value if validator provided
    if (validator && typeof validator === 'function') {
      const validation = validator(value);
      if (!validation.isValid) {
        return {
          value: defaultValue,
          isValid: false,
          error: validation.error || 'Validation failed'
        };
      }
      value = validation.value || value;
    }

    return {
      value: value,
      isValid: true,
      error: null
    };
  },

  /**
   * Store temporary data with expiration
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @param {number} expirationMs - Expiration time in milliseconds
   * @returns {boolean} True if successful
   */
  storeTemporary: function(key, value, expirationMs) {
    const expirationTime = Date.now() + expirationMs;
    const wrappedValue = {
      value: value,
      expiration: expirationTime
    };

    return sessionStorage.setItem(key, wrappedValue);
  },

  /**
   * Get temporary data (removes if expired)
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value
   * @returns {any} Stored value or default
   */
  getTemporary: function(key, defaultValue) {
    const wrappedValue = sessionStorage.getItem(key);
    if (!wrappedValue) return defaultValue;

    // Check expiration
    if (Date.now() > wrappedValue.expiration) {
      sessionStorage.removeItem(key);
      return defaultValue;
    }

    return wrappedValue.value;
  },

  /**
   * Get storage usage information
   * @returns {Object} Storage usage stats
   */
  getStorageInfo: function() {
    const info = {
      localStorage: {
        available: storageAvailable.localStorage,
        used: 0,
        keys: 0
      },
      sessionStorage: {
        available: storageAvailable.sessionStorage,
        used: 0,
        keys: 0
      }
    };

    // Calculate localStorage usage
    if (storageAvailable.localStorage) {
      try {
        const localKeys = localStorage.getAllKeys();
        info.localStorage.keys = localKeys.length;

        let localUsed = 0;
        localKeys.forEach(function(key) {
          const value = window.localStorage.getItem(key);
          localUsed += key.length + (value ? value.length : 0);
        });
        info.localStorage.used = localUsed;
      } catch (error) {
        console.error('Error calculating localStorage usage:', error);
      }
    }

    // Calculate sessionStorage usage
    if (storageAvailable.sessionStorage) {
      try {
        const sessionKeys = [];
        for (let i = 0; i < window.sessionStorage.length; i++) {
          sessionKeys.push(window.sessionStorage.key(i));
        }
        info.sessionStorage.keys = sessionKeys.length;

        let sessionUsed = 0;
        sessionKeys.forEach(function(key) {
          const value = window.sessionStorage.getItem(key);
          sessionUsed += key.length + (value ? value.length : 0);
        });
        info.sessionStorage.used = sessionUsed;
      } catch (error) {
        console.error('Error calculating sessionStorage usage:', error);
      }
    }

    return info;
  }
};

/**
 * Initialize storage system
 * @returns {Object} Initialization result
 */
export function initializeStorage() {
  // Check storage availability
  const availability = {
    localStorage: storageAvailable.localStorage,
    sessionStorage: storageAvailable.sessionStorage
  };

  // Perform migration if needed
  const migration = storageMigration.checkAndMigrate();

  // Log initialization in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📦 Storage initialized:', {
      localStorage: availability.localStorage,
      sessionStorage: availability.sessionStorage,
      migrated: migration.migrated,
      version: migration.toVersion
    });
  }

  return {
    availability: availability,
    migration: migration,
    timestamp: new Date().toISOString()
  };
}

/**
 * Export all storage utilities
 */
export default {
  local: localStorage,
  session: sessionStorage,
  arbiwin: arbiwinStorage,
  events: storageEvents,
  migration: storageMigration,
  utils: storageUtils,
  initialize: initializeStorage
};
